"use strict";
/**
 * The base module declares reusable base classes for items, lists, etc.
 * @preferred
 */
Object.defineProperty(exports, "__esModule", { value: true });
var service_1 = require("../service");
/**
 * Provides access to an item available on the EVA back-end
 */
var FetchableItem = /** @class */ (function () {
    /**
     * This class wraps an item from EVA and the operations you can perform on it.
     * You should supply the item identifier when creating an instance of this class
     *
     * @param  {number | string}    id      The identifier of the item to retrieve
     * @return {void}                       This method doesn't return anything
     */
    function FetchableItem(id) {
        this.id = id;
    }
    Object.defineProperty(FetchableItem.prototype, "id", {
        /**
         * Getter for the internal id property of this item so it is possible
         * to override the behaviour of determining the value of the id
         *
         * @return {number | string} the identifiable value of this object
         */
        get: function () {
            return this._id;
        },
        /**
         * Set for the id of the fetchable item. This can be overriden in a subclass so
         * additional behaviour can be implemented when setting the id
         *
         * @param  {number | string}      id An item identifier
         * @return {void}                    This item doesn't return anything
         */
        set: function (id) {
            this._id = id;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the current item identifier
     * @return {number | string} An item identifier
     */
    FetchableItem.prototype.getId = function () {
        return this._id;
    };
    /**
     * Returns the current item details. Only available after a successful fetch
     * The details are also exposed direct as the .data attribute for easy binding
     * @return {T} The item details
     */
    FetchableItem.prototype.getData = function () {
        return this.data;
    };
    /**
     * Sets the current item details using the fetched service response.
     * Extending classes should override this method to pull the details from response
     * @param  {RES} response The response from the service call
     * @return {T}            The item details
     */
    FetchableItem.prototype.setData = function (response) {
        // The default response is usually EVA.Core.GetResponse
        // An extending class will know exactly what is returned and where
        // the data for <T> can be found
        //
        return this.data = response.Result;
    };
    /**
     * Fetches the item details from the EVA back-end
     * @param  {string}  serviceName The name of the EVA Service to call
     * @param  {REQ}     requestData The request data for service call
     * @param  {string}  path        The request path (rest-like) if not using post message
     * @return {Promise<IEVAServiceResponse<REQ, RES>>} A promise resolving when the fetch from the back-end has completed
     */
    FetchableItem.prototype.fetch = function (serviceName, requestData, path, method) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService(serviceName, path, method);
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData).then(function (serviceResult) {
                _this.setData(serviceResult.response);
                resolve(serviceResult);
            })
                .catch(reject);
        });
    };
    return FetchableItem;
}());
exports.FetchableItem = FetchableItem;
/**
 * Provides access to a pageable list available on the EVA back-end
 */
var FetchablePagedList = /** @class */ (function () {
    /**
     * This class wraps a paged result list from EVA and the operations you can perform on it.
     * You can optionally supply page configuration values to start somewhere other then
     * the start of the list
     *
     * @param  {number}                         start           The item to start the list at. Defaults to 0 and is a 0-based index.
     * @param  {number}                         limit           The page size limit. Defaults to 15
     * @param  {EVA.Framework.SortDirection}  sortDirection   The sorting direction for the list. Defaults to ascending
     * @return {void}                                           This method doesn't return anything
     */
    function FetchablePagedList(limit, start, sortDirection, sortProperty) {
        if (limit === void 0) { limit = 15; }
        if (start === void 0) { start = 0; }
        if (sortDirection === void 0) { sortDirection = 0; }
        if (sortProperty === void 0) { sortProperty = "product_id"; }
        // Initialise the page configuration
        //
        this.pageConfig = {
            Start: start,
            Limit: limit,
            SortDirection: sortDirection,
            SortProperty: sortProperty,
            Filter: {}
        };
    }
    /**
     * Returns the current page size limit
     * @return {number}       The configured page size limit
     */
    FetchablePagedList.prototype.getLimit = function () {
        return this.pageConfig.Limit;
    };
    /**
     * Set the page size limit
     * @param  {number} limit The new page size limit
     * @return {number}       The configured page size limit
     */
    FetchablePagedList.prototype.setLimit = function (limit) {
        return this.pageConfig.Limit = limit;
    };
    /**
     * Return the current starting item for the list
     * @return {number}       The configured starting point for the list
     */
    FetchablePagedList.prototype.getStart = function () {
        return this.pageConfig.Start;
    };
    /**
     * Set the starting point for the list
     * @param  {number} start The new starting point for the list
     * @return {number}       The configured starting point for the list
     */
    FetchablePagedList.prototype.setStart = function (start) {
        return this.pageConfig.Start = start;
    };
    /**
     * Return the current property being sorted on
     * Can be undefined which means the server decides
     * @return {string}      The configured sorting property
     */
    FetchablePagedList.prototype.getSortProperty = function () {
        return this.pageConfig.SortProperty;
    };
    /**
     * Set the property to sort on
     * @param  {string} name The new sorting property
     * @return {string}      The configured sorting property
     */
    FetchablePagedList.prototype.setSortProperty = function (name) {
        return this.pageConfig.SortProperty = name;
    };
    /**
     * Returns the current sort direction
     * @return {string}      The configured sorting property
     */
    FetchablePagedList.prototype.getSortDirection = function () {
        return this.pageConfig.SortDirection;
    };
    /**
     * Sets the direction to sort the list on
     * @param  {EVA.Framework.SortDirection} direction The new sort direction
     * @return {string}      The configured sorting property
     */
    FetchablePagedList.prototype.setSortDirection = function (direction) {
        return this.pageConfig.SortDirection = direction;
    };
    /**
     * Set the filter that is to be applied to the list
     * @param  {any} filter The new filter structure
     * @return {any}        The configured filter
     */
    FetchablePagedList.prototype.setFilter = function (filter) {
        return this.pageConfig.Filter = filter;
    };
    /**
     * Returns the current filter for the list
     * @return {any}        The configured filter
     */
    FetchablePagedList.prototype.getFilter = function () {
        return this.pageConfig.Filter;
    };
    /**
     * Returns the current filter applied for the list
     * Only after available after fetch has been called
     * The applied filter can differ from the requested filter depending on how
     * the EVA back-end and its products are configured
     * @return {any}        The applied filter
     */
    FetchablePagedList.prototype.getAppliedFilter = function () {
        return this.appliedFilter;
    };
    /**
     * Returns all the current page configuration parameters
     * These determine which part of the list data is returned
     * @return {EVA.Framework.PageConfig} The current page configuration
     */
    FetchablePagedList.prototype.getPageConfiguration = function () {
        return this.pageConfig;
    };
    /**
     * Sets all the current page configuration parameters at once
     * These determine which part of the list data is returned
     * @param  {EVA.Framework.PageConfig} pageConfig The page configuration details
     * @return {EVA.Framework.PageConfig}            The new page configuration details
     */
    FetchablePagedList.prototype.setPageConfiguration = function (pageConfig) {
        return this.pageConfig = pageConfig;
    };
    /**
     * Returns the current page information which entails the current page, total number of pages and total number of items
     * @return {IPageInfo}  The current page information
     */
    FetchablePagedList.prototype.getPageInfo = function () {
        return {
            currentPageNumber: this.currentPageNumber,
            totalNumberOfPages: this.totalNumberOfPages,
            totalNumberOfItems: this.totalNumberOfItems
        };
    };
    /**
     * Returns the current page item details. Only available after a successful fetch
     * The details are also exposed direct as the .data attribute for easy binding
     * @return {Array<T>} An array of item details
     */
    FetchablePagedList.prototype.getData = function () {
        return this.data;
    };
    /**
     * Sets the current item details using the fetched service response.
     * Extending classes should override this method to pull the details from response
     * @param  {RES}        response    The response from the service call
     * @return {Array<T>}               The item details
     */
    FetchablePagedList.prototype.setData = function (response) {
        // The default response is usually EVA.Core.PagedResultGeneric
        // An extending class will know exactly what is returned and where
        // the data for <T> can be found
        //
        // Store the paging response information
        //
        this.currentPageNumber = response.Result.CurrentPage;
        this.totalNumberOfPages = response.Result.NumberOfPages;
        this.totalNumberOfItems = response.Result.Total;
        this.appliedFilter = response.Result.Filters;
        // Set and return the page items
        //
        return this.data = response.Result.Page;
    };
    /**
     * Fetches the list of items from the EVA back-end
     * @param  {string}  serviceName The name of the EVA Service to call
     * @param  {REQ}     requestData The request data for service call
     * @return {Promise<IEVAServiceResponse<REQ, RES>>} A promise resolving when the fetch from the back-end has completed
     */
    FetchablePagedList.prototype.fetch = function (serviceName, requestData, path, method) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService(serviceName, path, method);
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData).then(function (serviceResult) {
                _this.setData(serviceResult.response);
                resolve(serviceResult);
            })
                .catch(reject);
        });
    };
    return FetchablePagedList;
}());
exports.FetchablePagedList = FetchablePagedList;
